
import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  /** Enable horizontal scrolling on mobile viewports (< md). Defaults to false. */
  scrollable?: boolean;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, scrollable = false, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-12 items-center justify-center p-1.5 gap-token-xs",
      "rounded-[var(--radius-lg)]",
      "bg-[var(--tab-list-background)] backdrop-blur-sm",
      "border border-[var(--tab-list-border)]",
      "text-[var(--tab-text-inactive)]",
      scrollable && "overflow-x-auto md:overflow-visible scrollbar-hide flex-nowrap justify-start md:justify-center",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap px-token-md py-token-sm text-sm font-semibold",
      "rounded-[var(--radius-md)]",
      "transition-all duration-[var(--transition-duration)]",
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      // Inactive state
      "text-[var(--tab-text-inactive)]",
      "hover:text-[var(--color-primary-text)] hover:bg-white/5",
      // Active state - uses dedicated tab variables
      "data-[state=active]:bg-[linear-gradient(135deg,var(--tab-active-bg-start),var(--tab-active-bg-end))]",
      "data-[state=active]:text-[var(--tab-text-active)]",
      "data-[state=active]:shadow-token-card data-[state=active]:shadow-[var(--tab-active-shadow)]/25",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-token-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
